import { useState } from "react";
import { Button, Col, Tag, Tooltip, message } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { useTableUser } from "../hooks/useTableUser";
import { IUserResFind } from "../../../redux/@types";
import Table from "../../../shared/components/ui/Table";
import { usersService } from "../../../shared/services";
import { TableColumns } from "../../../shared/components/ui/Table/types";
import { useUsers } from "../hooks/useUsers";
import { refreshTableUsers } from "../../../redux";
import useAppDispatch from "../../../redux/hooks/useAppDispatch";
import { ModalUnlockUser } from "../../../shared/components";

const EMPTY = "—";

const displayValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") return EMPTY;
  return String(value);
};

const query = { $sort: { id: -1 } };

const formatUnlockDate = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("es-CO");
};

const ellipsisCell = {
  ellipsis: { showTitle: false as const },
  onCell: () => ({ className: "users-table-ellipsis" }),
};

const EllipsisText = ({ value }: { value: unknown }) => {
  const text = displayValue(value);
  if (text === EMPTY) return <>{EMPTY}</>;
  return (
    <Tooltip title={text} placement="topLeft">
      <span>{text}</span>
    </Tooltip>
  );
};

const STATUS_META: Record<
  string,
  { label: string; color: string; full: string }
> = {
  active: { label: "Activo", color: "success", full: "Activo" },
  inactive: { label: "Inactivo", color: "default", full: "Inactivo" },
  "pending security verification": {
    label: "Seguridad pendiente",
    color: "error",
    full: "Verificación de seguridad pendiente",
  },
  "pending user data": {
    label: "Datos pendientes",
    color: "warning",
    full: "Datos de usuario pendientes",
  },
  "pending validation": {
    label: "Validación pendiente",
    color: "processing",
    full: "Validación pendiente",
  },
};

export const TableUsers = () => {
  const dispatch = useAppDispatch();
  const { showDeleteConfirm, changeUserForUpdate } = useTableUser();
  const { setTableProps, tableProps } = useUsers();
  const [unlockTarget, setUnlockTarget] = useState<IUserResFind | null>(null);
  const [unlocking, setUnlocking] = useState(false);

  const unlockUser = async (reason: string) => {
    if (!unlockTarget) return;
    if (!reason) {
      message.warning("Indica el motivo del desbloqueo");
      return;
    }
    setUnlocking(true);
    try {
      await usersService.patch(unlockTarget.id, {
        status: "active",
        unlocked_reason: reason,
      });
      message.success(`Usuario ${unlockTarget.id} desbloqueado`);
      setUnlockTarget(null);
      dispatch(refreshTableUsers(true));
      tableProps?.refresh();
    } catch (err: any) {
      message.error(err?.message || "No se pudo desbloquear");
    } finally {
      setUnlocking(false);
    }
  };

  const columns: TableColumns<IUserResFind> = [
    {
      title: "ID",
      width: 80,
      dataIndex: "id",
      key: "id",
      filterType: "text",
    },
    {
      title: "Nombres",
      width: 130,
      dataIndex: "first_name",
      filterType: "data_value",
      key: "first_name",
      ...ellipsisCell,
      render: (_, item) => <EllipsisText value={item.first_name} />,
    },
    {
      title: "Apellidos",
      width: 140,
      dataIndex: "last_name",
      key: "last_name",
      filterType: "text",
      ...ellipsisCell,
      render: (_, item) => <EllipsisText value={item.last_name} />,
    },
    {
      title: "Cédula",
      dataIndex: "dni",
      key: "dni",
      filterType: "text",
      width: 130,
      render: (_, item) => displayValue(item.dni),
    },
    {
      title: "Ciudad",
      dataIndex: "city",
      key: "city",
      filterType: "text",
      width: 130,
      ...ellipsisCell,
      render: (_, item) => (
        <EllipsisText
          value={
            item.city ||
            (item as IUserResFind & { location_city?: { name?: string } })
              .location_city?.name
          }
        />
      ),
    },
    {
      title: "Correo",
      dataIndex: "email",
      key: "email",
      width: 230,
      filterType: "text",
      ...ellipsisCell,
      render: (_, item) => <EllipsisText value={item.email} />,
    },
    {
      title: "Rol",
      width: 120,
      dataIndex: "role",
      key: "role",
      render: (_, item) => {
        const roleLabels: Record<string, string> = {
          user: "Usuario",
          admin: "Admin",
          seller: "Vendedor",
          teacher: "Profesor",
          teacher_admin: "Profesor admin",
          domiciliary: "Domiciliario",
        };
        return displayValue(roleLabels[item.role] || item.role);
      },
      filters: [
        {
          text: "Usuario",
          value: "user",
        },
        {
          text: "Admin",
          value: "admin",
        },
        {
          text: "Vendedor",
          value: "seller",
        },
        {
          text: "Profesor",
          value: "teacher",
        },
        {
          text: "Profesor admin",
          value: "teacher_admin",
        },
      ],
    },
    {
      title: "Tipo",
      width: 120,
      dataIndex: "student_teacher",
      key: "student_teacher",
      render: (_, item) => {
        const typeLabels: Record<string, string> = {
          student: "Estudiante",
          teacher: "Profesor",
        };
        return displayValue(
          item.student_teacher ? typeLabels[item.student_teacher] : null
        );
      },
      filters: [
        {
          text: "Estudiante",
          value: "student",
        },
        {
          text: "Profesor",
          value: "teacher",
        },
      ],
    },
    {
      title: "Cód. estudiantil",
      width: 140,
      dataIndex: "student_code",
      key: "student_code",
      filterType: "text",
      render: (_, item) => displayValue(item.student_code),
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      width: 180,
      render: (_, item) => {
        const meta = STATUS_META[item.status];
        if (!meta) return displayValue(item.status);
        return (
          <Tooltip title={meta.full}>
            <Tag color={meta.color} style={{ margin: 0 }}>
              {meta.label}
            </Tag>
          </Tooltip>
        );
      },
      filters: [
        { value: "active", text: "Activo" },
        { value: "inactive", text: "Inactivo" },
        {
          value: "pending security verification",
          text: "verificación de seguridad pendiente",
        },
        { value: "pending user data", text: "datos de usuario pendientes" },
        { value: "pending validation", text: "Validación pendiente" },
      ],
    },
    {
      title: "Motivo bloqueo",
      dataIndex: "disabled_reason",
      key: "disabled_reason",
      width: 200,
      ...ellipsisCell,
      render: (_, item) => <EllipsisText value={item.disabled_reason} />,
    },
    {
      title: "Motivo desbloqueo",
      dataIndex: "unlocked_reason",
      key: "unlocked_reason",
      width: 200,
      ...ellipsisCell,
      render: (_, item) => <EllipsisText value={item.unlocked_reason} />,
    },
    {
      title: "Desbloqueado por",
      dataIndex: "unlocked_by_name",
      key: "unlocked_by_name",
      width: 220,
      ...ellipsisCell,
      render: (_, item) => {
        const who = displayValue(item.unlocked_by_name);
        const when = formatUnlockDate(item.unlocked_at);
        if (who === EMPTY) return EMPTY;
        return (
          <EllipsisText value={when ? `${who} · ${when}` : who} />
        );
      },
    },
    {
      title: "Última IP",
      dataIndex: "ip_address_last",
      key: "ip_address_last",
      width: 140,
      filterType: "text",
      render: (_, item) => displayValue(item.ip_address_last),
    },
    {
      title: "Teléfono",
      dataIndex: "phone",
      key: "phone",
      width: 130,
      filterType: "text",
      render: (_, item) => displayValue(item.phone),
    },
    {
      title: "Acciones",
      key: "operation",
      fixed: "right",
      width: 150,
      render: (_, item) => {
        return (
          <Col
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "space-evenly",
              flexDirection: "row",
              gap: 4,
            }}
          >
            <Button
              type="primary"
              shape="circle"
              onClick={() => changeUserForUpdate(item)}
              icon={<EditOutlined />}
              size="middle"
            />
            {item.status === "pending security verification" && (
              <Button
                type="default"
                shape="circle"
                icon={<UnlockOutlined />}
                size="middle"
                title="Desbloquear"
                onClick={() => setUnlockTarget(item)}
              />
            )}
            <Button
              type="primary"
              danger
              onClick={() =>
                showDeleteConfirm(
                  item.id,
                  `${item.first_name} ${item.last_name}`
                )
              }
              shape="circle"
              icon={<DeleteOutlined />}
              size="middle"
            />
          </Col>
        );
      },
    },
  ];

  return (
    <>
      <Table<IUserResFind>
        className="users-table"
        columns={columns}
        fetchQueryProps={query}
        service={usersService}
        onLoad={setTableProps}
        scroll={{ x: 2600 }}
      />
      <ModalUnlockUser
        user={unlockTarget}
        loading={unlocking}
        onCancel={() => setUnlockTarget(null)}
        onConfirm={unlockUser}
      />
    </>
  );
};
